/**
 * 音效系统
 * 使用 Web Audio API 生成简单音效，无需外部文件
 */
export class AudioSystem {
  constructor() {
    this.context = null;
    this.enabled = true;
    this.volume = 0.3;
    this.initialized = false;
  }
  
  /**
   * 初始化音频上下文
   */
  init() {
    if (this.initialized) return;
    
    try {
      this.context = new (window.AudioContext || window.webkitAudioContext)();
      this.initialized = true;
    } catch (e) {
      console.warn('Web Audio API not supported');
    }
  }
  
  /**
   * 确保上下文已初始化（处理自动播放策略）
   */
  ensureContext() {
    if (!this.initialized) {
      this.init();
    }
    if (this.context && this.context.state === 'suspended') {
      this.context.resume();
    }
  }
  
  /**
   * 播放预设音效
   * @param {string} type - 音效类型: 'click', 'success', 'error', 'win', 'flip', 'match'
   */
  play(type) {
    if (!this.enabled) return;
    this.ensureContext();
    if (!this.context) return;
    
    switch (type) {
      case 'click':
        this.playTone(800, 0.05);
        break;
      case 'success':
        this.playSuccess();
        break;
      case 'error':
        this.playTone(200, 0.2, 'sawtooth');
        break;
      case 'win':
        this.playWin();
        break;
      case 'flip':
        this.playTone(600, 0.1);
        break;
      case 'match':
        this.playMatch();
        break;
      case 'pop':
        this.playTone(400, 0.08);
        break;
      default:
        this.playTone(440, 0.1);
    }
  }
  
  /**
   * 播放指定频率的音效
   * @param {number} frequency - 频率(Hz)
   * @param {number} duration - 持续时间(秒)
   * @param {string} type - 波形类型: 'sine', 'square', 'sawtooth', 'triangle'
   */
  playTone(frequency, duration, type = 'sine') {
    if (!this.enabled || !this.context) return;
    
    const osc = this.context.createOscillator();
    const gain = this.context.createGain();
    
    osc.connect(gain);
    gain.connect(this.context.destination);
    
    osc.frequency.value = frequency;
    osc.type = type;
    
    const now = this.context.currentTime;
    gain.gain.setValueAtTime(this.volume, now);
    gain.gain.exponentialRampToValueAtTime(0.01, now + duration);
    
    osc.start(now);
    osc.stop(now + duration);
  }
  
  /**
   * 播放成功音效（上升音阶）
   */
  playSuccess() {
    if (!this.enabled || !this.context) return;
    
    const notes = [523.25, 659.25, 783.99]; // C5, E5, G5
    notes.forEach((freq, i) => {
      setTimeout(() => this.playTone(freq, 0.15), i * 100);
    });
  }
  
  /**
   * 播放匹配音效
   */
  playMatch() {
    if (!this.enabled || !this.context) return;
    
    const notes = [523.25, 659.25, 783.99]; // C5, E5, G5
    notes.forEach((freq, i) => {
      setTimeout(() => this.playTone(freq, 0.15), i * 80);
    });
  }
  
  /**
   * 播放胜利音效（完整旋律）
   */
  playWin() {
    if (!this.enabled || !this.context) return;
    
    const melody = [523.25, 659.25, 783.99, 1046.5, 783.99, 1046.5];
    melody.forEach((freq, i) => {
      setTimeout(() => this.playTone(freq, 0.2), i * 150);
    });
  }
  
  /**
   * 设置静音
   * @param {boolean} mute - 是否静音
   */
  setMute(mute) {
    this.enabled = !mute;
  }
  
  /**
   * 切换静音状态
   * @returns {boolean} 新的静音状态
   */
  toggleMute() {
    this.enabled = !this.enabled;
    return !this.enabled;
  }
  
  /**
   * 设置音量
   * @param {number} vol - 音量 0-1
   */
  setVolume(vol) {
    this.volume = Math.max(0, Math.min(1, vol));
  }
  
  /**
   * 获取当前音量
   * @returns {number}
   */
  getVolume() {
    return this.volume;
  }
  
  /**
   * 是否已启用
   * @returns {boolean}
   */
  isEnabled() {
    return this.enabled;
  }
}

// 单例导出
export const audio = new AudioSystem();
